import React, { useCallback, useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";

interface FileDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  onError?: (error: Error) => void;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedFileTypes?: string[];
  disabled?: boolean;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  helperText?: string;
}

const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const DEFAULT_ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export function FileDropzone({
  onFilesSelected,
  onError,
  maxFiles = 100,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  disabled = false,
  loading = false,
  title = "Drag & drop files here",
  subtitle = "or",
  buttonText = "Select Files",
  helperText = `Support PDF files, up to ${(maxFileSize / 1024 / 1024).toFixed(0)}MB each`,
}: FileDropzoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = useCallback(
    (files: File[]): { valid: File[]; errors: string[] } => {
      const valid: File[] = [];
      const errors: string[] = [];

      files.forEach((file) => {
        if (file.size > maxFileSize) {
          errors.push(
            `${file.name} exceeds maximum size of ${(maxFileSize / 1024 / 1024).toFixed(1)}MB`,
          );
        } else if (
          acceptedFileTypes.length > 0 &&
          !acceptedFileTypes.includes(file.type)
        ) {
          errors.push(`${file.name} has unsupported file type`);
        } else {
          valid.push(file);
        }
      });

      if (valid.length > maxFiles) {
        const excess = valid.length - maxFiles;
        valid.splice(valid.length - excess, excess);
        errors.push(
          `Maximum ${maxFiles} files allowed. ${excess} file(s) not added.`,
        );
      }

      return { valid, errors };
    },
    [maxFileSize, acceptedFileTypes, maxFiles],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled || loading) return;

      const { valid, errors } = validateFiles(acceptedFiles);

      if (valid.length > 0) {
        onFilesSelected(valid);
      }

      if (errors.length > 0) {
        errors.forEach((error) => {
          onError?.(new Error(error));
        });
      }
    },
    [onFilesSelected, validateFiles, onError, disabled, loading],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
    noKeyboard: true,
    disabled: disabled || loading,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: () => setDragActive(false),
    onDropRejected: () => setDragActive(false),
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    handleDrop(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isActive = dragActive || isDragActive;
  const isDisabled = disabled || loading;

  return (
    <Paper
      {...getRootProps()}
      sx={{
        p: 3,
        border: "2px dashed",
        borderColor: isActive ? "primary.main" : "divider",
        bgcolor: isActive ? "action.hover" : "background.paper",
        cursor: isDisabled ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        opacity: isDisabled ? 0.6 : 1,
        "&:hover": {
          borderColor: isDisabled ? "divider" : "primary.main",
          bgcolor: isDisabled ? "background.paper" : "action.hover",
        },
      }}
    >
      <input {...getInputProps()} />
      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={acceptedFileTypes.join(",")}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        disabled={isDisabled}
      />

      <Box textAlign="center">
        {loading ? (
          <CircularProgress sx={{ mb: 2 }} />
        ) : (
          <CloudUploadIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
          />
        )}

        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {subtitle}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={
            loading ? <CircularProgress size={16} /> : <CloudUploadIcon />
          }
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled}
          sx={{ mb: 2 }}
        >
          {loading ? "Processing..." : buttonText}
        </Button>

        <Typography variant="caption" display="block" color="text.secondary">
          {helperText}
        </Typography>
      </Box>
    </Paper>
  );
}
